import React from 'react';
import {
  Button,
  CircularProgressLoader,
  Modal,
  RemoveProfilePicture,
  ToastBar,
} from '../../components';
import { ChangePassword, UpdateProfileForm } from '../../forms';
import { compose } from 'redux';
import { injectIntl } from '../../utils/reactIntl';
import { connect } from 'react-redux';
import { MyProfileService } from '../../services';
import OtpVerification from '../../components/OtpVerification';
import './myprofile.global.scss';
import userprofile from '../../assets/svg/image-placeholder.png';
import edit from '../../assets/svg/edit.svg';
import { getUserUpdatedProfile } from '../../actions';
import '../LayoutWrapperMain/LayoutWrapperMain.global.scss';

interface Props {
  getProfileInfo: any;
  userProfile: any;
}

enum ButtonTypes {
  'button',
  'submit',
  'reset',
  undefined
}

const MyProfile: React.FC<Props> = (props: Props) => {
  const { getProfileInfo, userProfile } = props;
  const [image, setImage] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState('');
  const [showModal, setShowModal] = React.useState(false);
  const [showUploadOptions, setShowUploadOptions] = React.useState(false);
  const [showUpdateInfo, setShowUpdateInfo] = React.useState(false);
  const [updateProfile, setUpdateProfile] = React.useState(false);
  const [showOtp, setShowOtp] = React.useState(false);
  const [invalidOtp, setInvalidOtp] = React.useState(false);
  const [otpLoader, setOtpLoader] = React.useState(false);
  const [profileUpdatedValues, setProfileUpdatedValues] = React.useState<any>();
  const [updateProfileLoader, setUpdateProfileLoader] = React.useState<any>();
  const [disabledUpdateButton, setDisabledUpdateButton] = React.useState(false);
  const [showRemove, setShowRemove] = React.useState(false);
  const [updateProfileError, setUpdateProfileError] = React.useState({
    error: false,
    message: '',
  });
  const [showAlert, setShowAlert] = React.useState({
    show: false,
    message: '',
  });
  const { data, loadingInProgress } = userProfile;
  React.useEffect(() => {
    getProfileInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!image) {
      setImagePreview('');
      return undefined;
    }
    const preview = URL.createObjectURL(image);
    setImagePreview(preview);
    return () => URL.revokeObjectURL(preview);
  }, [image]);

  const handlePictureUpdate = async () => {
    if (!image || updateProfile || disabledUpdateButton) return;
    setUpdateProfile(true);
    setShowUploadOptions(false);
    setShowAlert({ show: false, message: '' });
    try {
      const fileData = new FormData();
      fileData.append('file', image);
      const response = await MyProfileService.uploadpicture(fileData);
      const url = response?.data?.data?.url;
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('Upload returned no picture URL');
      }
      await MyProfileService.updateProfile({ profilePicture: url });
      getProfileInfo();
      setShowAlert({ show: true, message: 'Profile picture changed successfully!!' });
      setDisabledUpdateButton(true);
    } catch (error: any) {
      const message = error?.response?.data?.message;
      setShowAlert({
        show: true,
        message: typeof message === 'string' && message.trim()
          ? message
          : 'Unable to update your profile picture. Please try again.',
      });
    } finally {
      setUpdateProfile(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setShowUpdateInfo(false);
    setShowOtp(false);
    setInvalidOtp(false);
    setUpdateProfileLoader(false);
    setUpdateProfileError({
      ...updateProfileError,
      error: false,
      message: '',
    });
    setShowRemove(false);
  };

  const handleOtpSubmit = (otp: any) => {
    setInvalidOtp(false);
    setOtpLoader(true);
    MyProfileService.verifyOTP({ otp }).then(() => {
      if (profileUpdatedValues?.name) {
        localStorage.setItem('username', profileUpdatedValues.name);
      }
      setShowAlert({
        ...showAlert,
        show: true,
        message: 'Profile updated successfully!!',
      });
      setOtpLoader(false);
      getProfileInfo();
      handleModalClose();
    }).catch(() => {
      setInvalidOtp(true);
      setOtpLoader(false);
    });
  };

  const handleUserProfileFormSubmit = (profileValues: any) => {
    setUpdateProfileLoader(true);
    setProfileUpdatedValues(profileValues);
    const normalizedFormCc = (profileValues.countryCode || '').replace('+', '');
    const normalizedSavedCc = (userProfile.data.countryCode || '').replace('+', '');
    if (profileValues.phoneNumber !== userProfile.data.phoneNo || normalizedFormCc !== normalizedSavedCc) {
      handleModalClose();
      MyProfileService.sendOTP({
        phoneNo: profileValues.phoneNumber,
        countryCode: profileValues.countryCode.replace('+', ''),
        name: profileValues.name,
      }).then(() => {
        setShowModal(true);
        setShowOtp(true);
        setShowUpdateInfo(false);
      }).catch((err: any) => {
        setUpdateProfileError({
          ...updateProfileError,
          error: true,
          message: err.response.data.message,
        });
      });
    } else {
      MyProfileService.updateProfile({ name: profileValues.name })
        .then(() => {
          localStorage.setItem('username', profileValues.name);
          setShowUpdateInfo(false);
          handleModalClose();
          getProfileInfo();
          setShowAlert({
            ...showAlert,
            show: true,
            message: 'Profile Updated successfully!!',
          });
        }).catch((error: any) => {
          setUpdateProfileLoader(false);
          setUpdateProfileError({
            error: true,
            message: error.response.data.message,
          });
        });
    }
  };

  return (
    <>
      <Modal
        id="edit-email-phone"
        isOpen={showModal}
        onClose={handleModalClose}
        onManageDisableScrolling={() => null}
        lightCloseButton={false}
      >
        {showUpdateInfo && (
          <UpdateProfileForm
            handleFormSubmit={handleUserProfileFormSubmit}
            userProfile={data}
            error={updateProfileError}
            handleModalClose={handleModalClose}
            updateProfileLoader={updateProfileLoader}
          />
        )}
        {showOtp && (
          <OtpVerification
            handleOtpSubmit={handleOtpSubmit}
            invalid={invalidOtp}
            handleResendOTP={() => MyProfileService.sendOTP({
              phoneNo: profileUpdatedValues.phoneNumber,
              countryCode: profileUpdatedValues.countryCode.replace('+', ''),
              name: profileUpdatedValues.name,
            })}
            showLoader={otpLoader}
          />
        )}
        {
          showRemove && (
            <RemoveProfilePicture
              handleModalClose={() => {
                setImage(null);
                handleModalClose();
                getProfileInfo();
              }}
            />
          )
        }
      </Modal>
      {showAlert.show
        && (
          <ToastBar
            message={showAlert.message}
            handleCloseMethod={() => {
              setShowAlert({
                ...showAlert,
                show: false,
                message: '',
              });
            }}
          />
        )}
      {loadingInProgress && data === null
        ? (
          <div className="loaderWrapper">
            <CircularProgressLoader />
          </div>
        ) : (
          <>
            <div className="cardBg">
              <div className="profilemain">
                <div className="profileBox">
                  <div className="w80per">
                    <div>
                      <div className="profileWrapperimg">
                        {image !== null
                          ? <img src={imagePreview} alt="Profile" />
                          : (
                            data !== null && data?.profilePicture !== '' && data?.profilePicture !== undefined
                              ? <img src={data?.profilePicture} alt="userprofile" />
                              : <img src={userprofile} alt="userprofile" />)}
                        <span className="profileEdit">
                          <input
                            type="button"
                            name="image"
                            id="image"
                            disabled={updateProfile}
                            aria-label="Change profile picture"
                            onClick={() => setShowUploadOptions((prevState: any) => !prevState)}
                          />
                          <img
                            src={edit}
                            alt="userprofile"
                          />
                        </span>
                        {showUploadOptions && (
                          <div>
                            <div>
                              <ul className="profileMenuopen">
                                <li>
                                  <span>
                                    <input
                                      type="file"
                                      name="image"
                                      id="image"
                                      title=""
                                      onChange={(e: any) => {
                                        if (e.target.files[0]) {
                                          setImage(e.target.files[0]);
                                          setDisabledUpdateButton(false);
                                        }
                                        setShowUploadOptions(false);
                                      }}
                                    />
                                  </span>
                                  Update Profile

                                </li>
                                <li>
                                  <span>
                                    <input
                                      type="button"
                                      name="image"
                                      id="image"
                                      onClick={() => {
                                        setShowRemove(true);
                                        setShowUploadOptions(false);
                                        setShowModal(true);
                                      }}
                                    />
                                  </span>
                                  Remove
                                </li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="profileWrapperdetails">
                        <div className="flex_row">
                          <div className="flex_col_sm_3">
                            <span>Name</span>
                          </div>
                          <div className="flex_col_sm_9">
                            <span><strong>{data?.name}</strong></span>
                          </div>
                          <div className="flex_col_sm_3">
                            <span>Email</span>
                          </div>
                          <div className="flex_col_sm_9">
                            <span><strong>{data?.email}</strong></span>
                          </div>
                          <div className="flex_col_sm_3">
                            <span> Phone No.</span>
                          </div>
                          <div className="flex_col_sm_9">
                            <span><strong>{`+${data?.countryCode}-${data?.phoneNo}`}</strong></span>
                          </div>
                          <div className="flex_col_sm_3">
                            <span>Title</span>
                          </div>
                          <div className="flex_col_sm_9">
                            <span>
                              <strong>
                                {data?.adminType}
                              </strong>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {updateProfile
                      ? (
                        <div>
                          <CircularProgressLoader />
                        </div>
                      ) : (
                        <>
                          <Button
                            buttonType={ButtonTypes.button}
                            onClick={handlePictureUpdate}
                            disabled={image === null || disabledUpdateButton}
                          >
                            Update
                          </Button>
                        </>
                      )}
                  </div>
                  <Button
                    className="buttoNot"
                    onClick={() => {
                      setShowModal(true);
                      setShowUpdateInfo(true);
                    }}
                  >
                    Edit Profile
                  </Button>
                </div>
              </div>
              <ChangePassword
                setShowAlert={setShowAlert}
                showAlert={showAlert}
              />
            </div>
          </>
        )}
    </>
  );
};
const mapStateToProps = (state: any) => ({
  userProfile: state.updateProfile,
});
const mapDispatchToProps = (dispatch: any) => ({
  getProfileInfo: () => dispatch(getUserUpdatedProfile()),
});

const MyProfileComponent = compose<React.ElementType>(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
)(MyProfile);

export default MyProfileComponent;
